import { useQueryClient } from '@tanstack/react-query';
import useGetApi from 'src/api/useGetApi';
import useMutationApi from 'src/api/useMutationApi';

export interface ManualAmount {
  id: string;
  ruleId: string;
  ruleName: string;
  divisionId: string;
  divisionName: string;
  amount: string; // DECIMAL field returned as string by Sequelize
  date: Date;
  description: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface CreateManualAmountRequest {
  ruleId: string;
  divisionId: string;
  amount: number;
  date: string; // ISO date string
  description: string;
}

export interface UpdateManualAmountRequest {
  amount?: number;
  date?: string; // ISO date string
  description?: string;
}

// Get manual amounts by agreement
export const useManualAmountsByAgreement = (
  agreementId: string,
  startDate?: string,
  endDate?: string,
  enabled = true,
) => {
  const params = new URLSearchParams();
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);

  return useGetApi<ManualAmount[]>({
    endpoint: `rebates/manual-amounts/agreement/${agreementId}?${params.toString()}`,
    options: {
      enabled: enabled && !!agreementId,
    },
  });
};

// Get manual amounts by rule
export const useManualAmountsByRule = (
  ruleId: string,
  startDate?: string,
  endDate?: string,
  enabled = true,
) => {
  const params = new URLSearchParams();
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);

  return useGetApi<ManualAmount[]>({
    endpoint: `rebates/manual-amounts/rule/${ruleId}?${params.toString()}`,
    options: {
      enabled: enabled && !!ruleId,
    },
  });
};

// Get single manual amount
export const useManualAmount = (id: string, enabled = true) => {
  return useGetApi<ManualAmount>({
    endpoint: `rebates/manual-amounts/${id}`,
    options: {
      enabled: enabled && !!id,
    },
  });
};

// Create manual amount
export const useCreateManualAmount = () => {
  const queryClient = useQueryClient();

  return useMutationApi<CreateManualAmountRequest, ManualAmount>({
    endpoint: 'rebates/manual-amounts',
    method: 'POST',
    successMessage: 'Manual amount created successfully',
    options: {
      onSuccess: () => {
        // Invalidate all manual amounts queries for this agreement
        queryClient.invalidateQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) &&
            typeof query.queryKey[0] === 'string' &&
            query.queryKey[0].includes('rebates/manual-amounts'),
        });
      },
    },
  });
};

// Update manual amount
export const useUpdateManualAmount = (id: string) => {
  const queryClient = useQueryClient();

  return useMutationApi<UpdateManualAmountRequest, ManualAmount>({
    endpoint: `rebates/manual-amounts/${id}`,
    method: 'PUT',
    successMessage: 'Manual amount updated successfully',
    options: {
      onSuccess: () => {
        // Invalidate all manual amounts queries for this agreement
        queryClient.invalidateQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) &&
            typeof query.queryKey[0] === 'string' &&
            query.queryKey[0].includes('rebates/manual-amounts'),
        });
      },
    },
  });
};

// Delete manual amount
export const useDeleteManualAmount = (id: string) => {
  const queryClient = useQueryClient();

  return useMutationApi<Record<string, never>>({
    endpoint: `rebates/manual-amounts/${id}`,
    method: 'DELETE',
    successMessage: 'Manual amount deleted successfully',
    options: {
      onSuccess: () => {
        // Invalidate all manual amounts queries for this agreement
        queryClient.invalidateQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) &&
            typeof query.queryKey[0] === 'string' &&
            query.queryKey[0].includes('rebates/manual-amounts'),
        });
      },
    },
  });
};

export default useManualAmountsByAgreement;
